import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import { money } from "./MemberDetail";

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const shift = (m, n) => { const [y, mo] = m.split("-").map(Number); return ym(new Date(y, mo - 1 + n, 1)); };
const label = (m) => { const [y, mo] = m.split("-").map(Number); return new Date(y, mo - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" }); };
const when = (s) => (s ? new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Meter({ title, used, included, unit, charges, testid }) {
  const pct = included ? Math.min(100, (used / included) * 100) : 0;
  const over = included && used > included;
  return (
    <div className="rounded-lg border border-brand-border bg-brand-surface p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary">{title}</p>
      <p className="text-2xl text-brand-ink mt-1">{used}<span className="text-sm text-brand-muted"> {included ? `of ${included} ${unit} included` : unit}</span></p>
      {included > 0 && <div className="h-1.5 rounded-full bg-brand-hover mt-3 overflow-hidden"><div className={`h-full rounded-full ${over ? "bg-rose-500" : "bg-brand-gold"}`} style={{ width: `${pct}%` }} /></div>}
      <p className="text-xs text-brand-muted mt-2">{charges ? `${money(charges)} billed` : "No extra charges"}</p>
    </div>
  );
}

function BookingList({ rows, testid, empty }) {
  if (!rows.length) return <p className="text-sm text-brand-tertiary px-1" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <div className="rounded-lg border border-brand-border bg-brand-surface divide-y divide-brand-border max-h-64 overflow-y-auto" data-testid={testid}>
      {rows.map((b) => (
        <div key={b.id} className="px-4 py-2 flex items-center justify-between gap-3 text-xs">
          <span className="min-w-0 truncate text-brand-ink">{b.space}<span className="text-brand-tertiary"> · {b.person}{b.location ? `, ${b.location}` : ""}</span></span>
          <span className="shrink-0 text-brand-muted">{when(b.start)}{b.hours ? ` · ${b.hours}h` : ""}{b.amount ? ` · ${money(b.amount)}` : ""}{b.upcoming ? " · upcoming" : ""}</span>
        </div>
      ))}
    </div>
  );
}

export default function MemberUsage({ companyId }) {
  const [month, setMonth] = useState(ym(new Date()));
  const [u, setU] = useState(null);
  useEffect(() => {
    setU(null);
    api.get(`/members/${companyId}/usage`, { params: { month } }).then(({ data }) => setU(data)).catch(() => setU(false));
  }, [companyId, month]);

  return (
    <div className="space-y-4" data-testid="member-usage">
      <div className="flex items-center gap-2">
        <button onClick={() => setMonth(shift(month, -1))} data-testid="usage-prev-month" className="p-1 rounded hover:bg-brand-hover"><ChevronLeft className="h-4 w-4" /></button>
        <span className="text-sm text-brand-ink min-w-[120px] text-center" data-testid="usage-month">{label(month)}</span>
        <button onClick={() => setMonth(shift(month, 1))} disabled={month >= ym(new Date())} data-testid="usage-next-month" className="p-1 rounded hover:bg-brand-hover disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {u === null && <p className="text-sm text-brand-muted flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading bookings from Yardi…</p>}
      {u === false && <p className="text-sm text-brand-muted">Couldn't load bookings.</p>}
      {u && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Meter title="Conference rooms" used={u.rooms.hours} included={u.rooms.included_hours} unit="hrs" charges={u.rooms.charges} testid="usage-rooms-meter" />
            <Meter title="Day passes" used={u.day_passes.count} included={u.day_passes.included} unit="passes" charges={u.day_passes.charges} testid="usage-daypass-meter" />
          </div>
          <BookingList rows={u.rooms.bookings} testid="usage-room-bookings" empty="No conference room bookings this month." />
          {u.day_passes.bookings.length > 0 && <BookingList rows={u.day_passes.bookings} testid="usage-daypass-bookings" empty="" />}
        </>
      )}
    </div>
  );
}
